import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { ChatMessage, Conversation, ChatAttachment } from '../types';
import { db } from '../lib/firebase';
import { useAuth } from './AuthContext';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  deleteDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';

import { getApiUrl } from '../lib/env';

const API_BASE = getApiUrl();

interface ChatContextType {
  conversations: Conversation[];
  currentConversation: Conversation | null;
  messages: ChatMessage[];
  isTyping: boolean;
  isLoading: boolean;
  streamingContent: string;
  error: string | null;
  openChat: (replicaId: string) => Promise<void>;
  sendMessage: (content: string, attachments?: ChatAttachment[], chatOptions?: { modelTier?: string; webSearch?: boolean; extendedThinking?: boolean }) => Promise<void>;
  closeChat: () => void;
  deleteConversation: (conversationId: string) => Promise<void>;
  renameConversation: (conversationId: string, title: string) => Promise<void>;
  createNewChat: (replicaId: string) => Promise<void>;
  switchConversation: (conversationId: string) => void;
  stopGeneration: () => void;
  clearError: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track fallback listener for proper cleanup (Issue #2 fix)
  const fallbackUnsubRef = useRef<(() => void) | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // Listen to all conversations for this user
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', user.uid),
      orderBy('lastMessageAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convos: Conversation[] = (snapshot.docs || []).map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
        lastMessageAt: d.data().lastMessageAt?.toDate?.()?.toISOString() || undefined,
      })) as Conversation[];
      setConversations(convos || []);
    }, (err) => {
      console.warn('[ChatContext] Primary query failed (composite index may be needed):', err.message);
      // Fallback: simpler query without orderBy
      const fallbackQ = query(
        collection(db, 'conversations'),
        where('participants', 'array-contains', user.uid)
      );
      // Fix #2: Track fallback unsubscribe for cleanup
      fallbackUnsubRef.current = onSnapshot(fallbackQ, (snapshot) => {
        const convos: Conversation[] = (snapshot.docs || []).map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
          lastMessageAt: d.data().lastMessageAt?.toDate?.()?.toISOString() || undefined,
        })) as Conversation[];
        // Sort manually since we can't use orderBy
        convos.sort((a, b) => new Date(b.lastMessageAt || b.createdAt).getTime() - new Date(a.lastMessageAt || a.createdAt).getTime());
        setConversations(convos);
      }, (err2) => {
        console.error('[ChatContext] Fallback query also failed:', err2.message);
        setError('Unable to load conversations. Please try again later.');
        setConversations([]);
      });
    });

    return () => {
      unsubscribe();
      // Fix #2: Clean up fallback listener too
      fallbackUnsubRef.current?.();
      fallbackUnsubRef.current = null;
    };
  }, [user?.uid]);

  // Listen to messages for current conversation
  useEffect(() => {
    if (!currentConversation?.id) {
      setMessages([]);
      return;
    }

    const q = query(
      collection(db, 'conversations', currentConversation.id, 'messages'),
      orderBy('timestamp', 'asc'),
      limit(200)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs: ChatMessage[] = (snapshot.docs || []).map((d) => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.()?.toISOString() || new Date().toISOString(),
      })) as ChatMessage[];
      setMessages(msgs || []);
    }, (err) => {
      console.error('[ChatContext] Failed to load messages:', err.message);
      setError('Failed to load messages for this conversation.');
    });

    return () => unsubscribe();
  }, [currentConversation?.id]);

  const openChat = useCallback(async (replicaId: string) => {
    if (!user?.uid) return;
    setIsLoading(true);
    setError(null);

    try {
      let existing = conversations.find(
        (c) => c.replicaId === replicaId && c.humanId === user.uid
      );

      // If conversations array is empty (Firestore listener hasn't synced yet),
      // do a direct query to avoid creating duplicate conversations
      if (!existing && conversations.length === 0) {
        try {
          const directQ = query(
            collection(db, 'conversations'),
            where('participants', 'array-contains', user.uid),
            where('replicaId', '==', replicaId),
            limit(1)
          );
          const snapshot = await getDocs(directQ);
          if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            existing = {
              id: docSnap.id,
              ...docSnap.data(),
              createdAt: docSnap.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
              lastMessageAt: docSnap.data().lastMessageAt?.toDate?.()?.toISOString() || undefined,
            } as Conversation;
          }
        } catch (queryErr: any) {
          console.warn('[ChatContext] Direct conversation query failed:', queryErr.message);
          // Continue to create — will fail if permissions don't allow it
        }
      }

      if (existing) {
        setCurrentConversation(existing);
      } else {
        // Create new conversation with auto-generated unique ID
        const newConvo: Omit<Conversation, 'id'> = {
          replicaId,
          humanId: user.uid,
          participants: [user.uid, replicaId],
          createdAt: new Date().toISOString(),
          title: 'New conversation',
        };

        const docRef = await addDoc(collection(db, 'conversations'), {
          ...newConvo,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
        });

        console.log('[ChatContext] Created conversation:', docRef.id);
        setCurrentConversation({ ...newConvo, id: docRef.id });
      }
    } catch (err: any) {
      console.error('[ChatContext] Failed to open chat:', err);
      setError(`Failed to open chat: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid, conversations]);

  const createNewChat = useCallback(async (replicaId: string) => {
    if (!user?.uid) return;
    setIsLoading(true);
    setError(null);

    try {
      const newConvo: Omit<Conversation, 'id'> = {
        replicaId,
        humanId: user.uid,
        participants: [user.uid, replicaId],
        createdAt: new Date().toISOString(),
        title: 'New conversation',
      };

      const docRef = await addDoc(collection(db, 'conversations'), {
        ...newConvo,
        createdAt: serverTimestamp(),
        lastMessageAt: serverTimestamp(),
      });

      setCurrentConversation({ ...newConvo, id: docRef.id });
      setMessages([]);
    } catch (err: any) {
      console.error('[ChatContext] Failed to create new chat:', err);
      setError(`Failed to create conversation: ${err?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [user?.uid]);

  const switchConversation = useCallback((conversationId: string) => {
    const convo = conversations.find(c => c.id === conversationId);
    if (convo) {
      setCurrentConversation(convo);
      setError(null);
    }
  }, [conversations]);

  const deleteConversation = useCallback(async (conversationId: string) => {
    try {
      // Delete all messages in the conversation's subcollection
      const messagesRef = collection(db, 'conversations', conversationId, 'messages');
      const messagesSnap = await getDocs(messagesRef);
      const deletePromises = messagesSnap.docs.map(d => deleteDoc(d.ref));
      await Promise.all(deletePromises);

      // Delete the conversation document
      await deleteDoc(doc(db, 'conversations', conversationId));

      // Clear current conversation if it was the deleted one
      if (currentConversation?.id === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (err: any) {
      console.error('[ChatContext] Failed to delete conversation:', err);
      setError(`Failed to delete conversation: ${err?.message || 'Unknown error'}`);
    }
  }, [currentConversation?.id]);

  const renameConversation = useCallback(async (conversationId: string, title: string) => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), { title });
    } catch (err: any) {
      console.error('[ChatContext] Failed to rename conversation:', err);
      setError(`Failed to rename: ${err?.message || 'Unknown error'}`);
    }
  }, []);

  const stopGeneration = useCallback(() => {
    if (abortController) {
      abortController.abort();
      setAbortController(null);
      setIsTyping(false);
      setStreamingContent('');
    }
  }, [abortController]);

  const sendMessage = useCallback(async (content: string, attachments?: ChatAttachment[], chatOptions?: { modelTier?: string; webSearch?: boolean; extendedThinking?: boolean }) => {
    if (!currentConversation || !user?.uid || !content.trim()) return;

    const conversationId = currentConversation.id;
    const replicaId = currentConversation.replicaId;
    setError(null);

    // Write user message to Firestore
    const userMsg = {
      conversationId,
      senderId: user.uid,
      senderType: 'human',
      content: content.trim(),
      channel: 'web',
      timestamp: serverTimestamp(),
      status: 'sent',
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
    };

    try {
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), userMsg);
    } catch (err: any) {
      console.error('[ChatContext] Failed to save message:', err);
      setError(`Failed to send message: ${err?.message || 'Unknown error'}`);
      return;
    }

    // Auto-title: only set title if the conversation still has the default placeholder title
    const needsTitle = !currentConversation.title || currentConversation.title === 'New conversation';
    const titleUpdate = needsTitle
      ? { lastMessage: content.trim(), lastMessageAt: serverTimestamp(), title: content.trim().slice(0, 60) }
      : { lastMessage: content.trim(), lastMessageAt: serverTimestamp() };

    await setDoc(doc(db, 'conversations', conversationId), titleUpdate, { merge: true });

    // Stream AI response via SSE
    setIsTyping(true);
    setStreamingContent('');
    let fullReply = '';
    let workflowMeta: any = null;
    let documentMeta: any = null;
    let actionMeta: any = null;
    let significanceMeta: any = null;

    const controller = new AbortController();
    setAbortController(controller);

    try {
      const token = await user.getIdToken?.();

      // Fix #8: Use POST for streaming to avoid URL length limits
      let streamUrl = `${API_BASE}/chat/${encodeURIComponent(replicaId)}/stream?message=${encodeURIComponent(content.trim())}`;

      // Append chat toolbar options as query params
      if (chatOptions?.modelTier) streamUrl += `&modelTier=${encodeURIComponent(chatOptions.modelTier)}`;
      if (chatOptions?.webSearch) streamUrl += `&webSearch=true`;
      if (chatOptions?.extendedThinking) streamUrl += `&extendedThinking=true`;

      // Build conversation history for context (last 20 messages)
      const historyForStream = messages.slice(-20).map((m) => ({
        role: m.senderType === 'human' ? 'user' : 'assistant',
        content: m.content,
      }));
      const historyHeader = btoa(unescape(encodeURIComponent(JSON.stringify(historyForStream))));

      const response = await fetch(streamUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
          'X-Chat-History': historyHeader,
        },
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        // Fallback to non-streaming POST
        const postResponse = await fetch(`${API_BASE}/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            replicaId,
            message: content.trim(),
            conversationId,
            conversationHistory: messages.slice(-20).map((m) => ({
              role: m.senderType === 'human' ? 'user' : 'assistant',
              content: m.content,
            })),
            ...(chatOptions ? { chatOptions } : {}),
          }),
          signal: controller.signal,
        });
        if (postResponse.ok) {
          const data = await postResponse.json();
          fullReply = data.reply || '';
          // Capture workflow metadata if present
          if (data.workflowMeta) {
            workflowMeta = data.workflowMeta;
          }
          if (data.documentMeta) {
            documentMeta = data.documentMeta;
          }
          if (data.actionMeta) {
            actionMeta = data.actionMeta;
          }
        } else {
          const errText = await postResponse.text().catch(() => 'Unknown error');
          throw new Error(`AI response failed (${postResponse.status}): ${errText}`);
        }
      } else if (response.headers.get('content-type')?.includes('application/json')) {
        // Document/action generation: stream endpoint returned JSON instead of SSE
        const data = await response.json();
        fullReply = data.reply || '';
        if (data.documentMeta) {
          documentMeta = data.documentMeta;
        }
        if (data.actionMeta) {
          actionMeta = data.actionMeta;
        }
        if (data.workflowMeta) {
          workflowMeta = data.workflowMeta;
        }
      } else {
        // Parse SSE stream
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const payload = JSON.parse(line.slice(6));
              if (payload.done) {
                // Capture significance metadata from the done event
                if (payload.significance) {
                  significanceMeta = payload.significance;
                }
                break;
              }
              if (payload.error) {
                console.error('Stream error:', payload.error);
                break;
              }
              if (payload.token) {
                fullReply += payload.token;
                setStreamingContent(fullReply);
              }
            } catch {
              // skip malformed lines
            }
          }
        }
      }

      // Write complete replica response to Firestore
      if (fullReply) {
        const replicaMsgData: Record<string, any> = {
          conversationId,
          senderId: replicaId,
          senderType: 'replica',
          content: fullReply,
          channel: 'web',
          timestamp: serverTimestamp(),
          status: 'delivered',
        };
        if (workflowMeta) {
          replicaMsgData.workflowMeta = workflowMeta;
        }
        if (documentMeta) {
          replicaMsgData.documentMeta = documentMeta;
        }
        if (actionMeta) {
          replicaMsgData.actionMeta = actionMeta;
        }
        if (significanceMeta) {
          replicaMsgData.significance = significanceMeta;
        }
        await addDoc(collection(db, 'conversations', conversationId, 'messages'), replicaMsgData);

        await setDoc(doc(db, 'conversations', conversationId), {
          lastMessage: fullReply,
          lastMessageAt: serverTimestamp(),
        }, { merge: true });
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        // User stopped generation
        if (fullReply) {
          await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
            conversationId,
            senderId: replicaId,
            senderType: 'replica',
            content: fullReply + '\n\n*[Generation stopped]*',
            channel: 'web',
            timestamp: serverTimestamp(),
            status: 'delivered',
          });
        }
      } else {
        console.error('[ChatContext] Failed to get AI response:', error);
        setError(`AI response failed: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setIsTyping(false);
      setStreamingContent('');
      setAbortController(null);
    }
  }, [currentConversation, user, messages]);

  const closeChat = useCallback(() => {
    setCurrentConversation(null);
    setMessages([]);
    setError(null);
  }, []);

  return (
    <ChatContext.Provider
      value={{
        conversations,
        currentConversation,
        messages,
        isTyping,
        isLoading,
        streamingContent,
        error,
        openChat,
        sendMessage,
        closeChat,
        deleteConversation,
        renameConversation,
        createNewChat,
        switchConversation,
        stopGeneration,
        clearError,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
